// src/templates/PageDatasetsContext.tsx
import { createContext, useContext } from 'react';

/**
 * Large content lists (FAQs, blogs, products, ...) used to come from
 * useStaticQuery, which Gatsby attaches to every page built from Page.tsx.
 * They are now part of the page query instead, gated per page with @include
 * flags that gatsby-node sets from the components each page actually renders.
 * Page.tsx puts the page query result here so the DataUtils hooks can read it.
 */
export type PageDatasets = Record<string, any> & {
    blogScrollSelections?: Record<string, string[]>;
};

export const PageDatasetsContext = createContext<PageDatasets>({});

export const usePageDatasets = (): PageDatasets =>
    useContext(PageDatasetsContext);

// An omitted query is a wiring error; a queried list with zero nodes is valid.
// Throw during SSR too, so a build cannot silently emit incomplete pages.
export function requirePageDataset(
    datasets: PageDatasets,
    name: string,
    required = true
): any {
    const dataset = datasets[name];
    if (required && dataset == null) {
        const page = datasets.kontentItemLayoutMasterPage;
        const slug = page?.elements?.slug?.value ?? 'unknown page';
        const language = page?.preferred_language ?? 'unknown language';
        throw new Error(
            `Missing page dataset "${name}" for "${slug}" (${language}). ` +
                'Check getPageDatasetContext, the Page query, and PageDatasetsContext.Provider.'
        );
    }
    return dataset;
}

export const usePageDataset = (name: string, required = true): any =>
    requirePageDataset(usePageDatasets(), name, required);
